/**
 * SSOT Registry — the data-driven catalog itself.
 *
 * Every entry reads its values LIVE via the SSOT's public accessors — no
 * value is copied into this file. Adding/removing a tier, changing a DOK
 * floor, or registering a question type is reflected here automatically.
 */

import { QuestionType } from '../../content/types';
import { QUESTION_TYPE_META } from '../../content/questions/questionTypeRegistry';
import { QUESTION_TYPE_VISUAL_ROLE } from '../../content/questions/questionTypeRegistry';
import { getAllTiers } from '../../content/taxonomy/tiers';
import { MODULES } from '../../content/modules';
import { TAXONOMY } from '../../content/taxonomy/taxonomyRegistry';
import { VISUAL_REGISTRY } from '../../visuals';
import { STAGES } from '../pipeline/stageDefinitions';
import { ISSUE_CODES } from '../pipeline/issueContract';
import { SEVERITY, DEFAULT_SCORING_WEIGHTS } from '../pipeline/severityScale';
import { getCognitiveBoundsForType } from '../../shared/proxyContracts/cognitiveSignals';
import { MIN_DOK_BY_TIER_BAND, MAX_DOK_BY_TIER_BAND, getModuleCognitiveProfiles, getDokFloorByProfile, type TierBand } from '../tierBands';
import { QUESTION_TYPE_REGISTRY, getDok3CapableTypes, getTypesRequiringStructuredData } from '../questionTypeDefinitions';
// Geen designTokens-import meer: de design-entries zijn verhuisd naar
// DesignSystemPage. Zie de toelichting bij het DESIGN SYSTEM-blok hieronder.
import { V11_CONFIG } from '../../mastery/selection/selectionConfig';
import { V11_CONFIG_FIELDS } from '../../mastery/selection/v11ConfigStore';
import { getGrowthStage } from '../../mastery/growthStages';
import { DIFFICULTY_INDEX_MIN, DIFFICULTY_INDEX_MAX, irtDifficultyToCitoPValue, toIRTDifficulty } from '../../shared/proxyContracts/difficulty';
import type { SsotEntry } from './types';

/** All question-type keys, derived from the runtime registry (SSOT for "which types exist"). */
const ALL_QUESTION_TYPES = Object.keys(QUESTION_TYPE_META) as QuestionType[];

/** Tier-band keys, derived from the floor map so the list can never drift. */
const TIER_BANDS = Object.keys(MIN_DOK_BY_TIER_BAND) as TierBand[];

/**
 * The catalog. Ordered by domain for readable admin grouping.
 *
 * NOTE: `read()` runs at call time — keep it pure and cheap (no I/O).
 */
const SSOT_ENTRIES: SsotEntry[] = [
// ─── TAXONOMY ──────────────────────────────────────────────────────────
{
  id: 'tier-system',
  name: '16-Tier Niveausysteem',
  domain: 'taxonomy',
  file: 'src/content/taxonomy/tiers.ts',
  exportName: 'TIERS / getAllTiers()',
  description: 'De 16 niveaus (B6 t/m E8+) met groep, periode en plus-variant.',
  governs: 'Tier→niveaucode mapping, groep-tier-gating, en de basis voor alle tier-band-afgeleiden.',
  consumers: ['tierBands.getTierBand', 'selection/tierWindow', 'assessmentBlueprintGenerator', 'mastery/tierProgress'],
  read: () => ({
    columns: [{
      key: 'tier',
      label: 'Tier'
    }, {
      key: 'levelCode',
      label: 'Niveau'
    }, {
      key: 'group',
      label: 'Groep'
    }, {
      key: 'period',
      label: 'Periode'
    }, {
      key: 'isPlus',
      label: 'Plus'
    }, {
      key: 'title',
      label: 'Titel'
    }],
    rows: getAllTiers().map(t => ({
      tier: t.tier,
      levelCode: t.levelCode,
      group: t.group,
      period: t.period,
      isPlus: t.isPlus ? 'ja' : 'nee',
      title: t.title
    }))
  })
},
// ─── COGNITIVE / DOK ───────────────────────────────────────────────────
{
  id: 'min-dok-by-tier-band',
  name: 'DOK-floor per tier-band (default)',
  domain: 'cognitive',
  file: 'src/governance/tierBands.ts',
  exportName: 'MIN_DOK_BY_TIER_BAND',
  description: 'Procedural-math default DOK-floor per tier-band. Fallback voor modules zonder profiel.',
  governs: 'De default-tak van getMinDokForBand() — basis voor PEDAGOGY_DOK_TOO_LOW.',
  consumers: ['getMinDokForBand', 'selectionConfig.MIN_DOK_FOR_TIER_BAND (alias)'],
  notes: 'Niet langer de floor-beslissing zelf — die loopt sinds P4 via getMinDokForBand(band, module).',
  read: () => ({
    columns: [{
      key: 'band',
      label: 'Tier-band'
    }, {
      key: 'minDok',
      label: 'Min DOK'
    }],
    rows: TIER_BANDS.map(band => ({
      band,
      minDok: MIN_DOK_BY_TIER_BAND[band]
    }))
  })
}, {
  id: 'max-dok-by-tier-band',
  name: 'DOK-ceiling per tier-band',
  domain: 'cognitive',
  file: 'src/governance/tierBands.ts',
  exportName: 'MAX_DOK_BY_TIER_BAND',
  description: '"Natuurlijk verwachte" maximale DOK per tier-band (coach-signaal).',
  governs: 'PEDAGOGY_DOK_TOO_HIGH (informational/coach, onder de gate-drempel).',
  consumers: ['pedagogyStage', 'pedagogicalGuard'],
  notes: 'Niet module-bewust (§16.4) — symmetrie met de module-bewuste floor ontbreekt; shadow-gated.',
  read: () => ({
    columns: [{
      key: 'band',
      label: 'Tier-band'
    }, {
      key: 'maxDok',
      label: 'Max DOK'
    }],
    rows: TIER_BANDS.map(band => ({
      band,
      maxDok: MAX_DOK_BY_TIER_BAND[band]
    }))
  })
}, {
  id: 'module-cognitive-profiles',
  name: 'Module cognitieve profielen',
  domain: 'cognitive',
  file: 'src/governance/tierBands.ts',
  exportName: 'MODULE_COGNITIVE_PROFILES',
  description: 'Classificeert elke module naar cognitief profiel (procedural-math / application-language / recall-knowledge / comprehension).',
  governs: 'Module-bewuste DOK-floor via DOK_FLOOR_BY_PROFILE.',
  consumers: ['getMinDokForBand'],
  read: () => {
    const profiles = getModuleCognitiveProfiles();
    return {
      columns: [{
        key: 'module',
        label: 'Module'
      }, {
        key: 'profile',
        label: 'Cognitief profiel'
      }],
      rows: Object.entries(profiles).map(([module, profile]) => ({
        module,
        profile
      }))
    };
  }
}, {
  id: 'dok-floor-by-profile',
  name: 'DOK-floor matrix per profiel',
  domain: 'cognitive',
  file: 'src/governance/tierBands.ts',
  exportName: 'DOK_FLOOR_BY_PROFILE',
  description: 'De DOK-floor per (cognitief profiel, tier-band).',
  governs: 'De daadwerkelijke floor-waarde die getMinDokForBand() teruggeeft.',
  consumers: ['getMinDokForBand'],
  read: () => {
    const matrix = getDokFloorByProfile();
    return {
      columns: [{
        key: 'profile',
        label: 'Profiel'
      }, ...TIER_BANDS.map(band => ({
        key: band,
        label: band
      }))],
      rows: Object.entries(matrix).map(([profile, byBand]) => ({
        profile,
        ...Object.fromEntries(TIER_BANDS.map(band => [band, byBand[band]]))
      }))
    };
  }
}, {
  id: 'cognitive-type-bounds',
  name: 'Structurele cognitieve bounds per vraagtype',
  domain: 'cognitive',
  file: 'src/shared/proxyContracts/cognitiveSignals.ts',
  exportName: 'TYPE_FLOOR / TYPE_CEILING (via getCognitiveBoundsForType)',
  description: 'Structurele [floor, ceiling] per vraagtype. Gedeelde DOK↔Bloom-bron, gelezen door cognitiveLevel.',
  governs: 'De clamp-window in cognitiveLevel.detectCognitiveLevelDetailed.',
  consumers: ['cognitiveLevel', 'clampToCognitiveBounds'],
  notes: 'Ceiling-drift met de sidecar cognitiveCeiling (§16.13) — multiple_choice: extended/DOK4 hier vs DOK2 sidecar. Shadow-gated.',
  read: () => ({
    columns: [{
      key: 'type',
      label: 'Vraagtype'
    }, {
      key: 'floor',
      label: 'Floor'
    }, {
      key: 'floorDok',
      label: 'Floor DOK'
    }, {
      key: 'ceiling',
      label: 'Ceiling'
    }, {
      key: 'ceilingDok',
      label: 'Ceiling DOK'
    }],
    rows: ALL_QUESTION_TYPES.map(type => {
      const b = getCognitiveBoundsForType(type);
      return {
        type,
        floor: b.floor.tier,
        floorDok: b.floor.dok,
        ceiling: b.ceiling.tier,
        ceilingDok: b.ceiling.dok
      };
    })
  })
},
// ─── GOVERNANCE / QUESTION TYPES ─────────────────────────────────────────
{
  id: 'question-type-ceilings',
  name: 'Cognitive ceiling per vraagtype (sidecar)',
  domain: 'governance',
  file: 'src/governance/questionTypeDefinitions.ts',
  exportName: 'QUESTION_TYPE_REGISTRY[].cognitiveCeiling',
  description: 'Hoogste DOK die een vraagtype structureel kan bereiken. Bron voor de autotagger-clamp.',
  governs: 'dokAutoTagger ceiling-clamp (GSR-17) + afgeleide DOK3-capable set.',
  consumers: ['dokAutoTagger.estimateDok', 'diversityStage (DIV-005)'],
  read: () => ({
    columns: [{
      key: 'type',
      label: 'Vraagtype'
    }, {
      key: 'ceiling',
      label: 'Cognitive ceiling'
    }, {
      key: 'category',
      label: 'Categorie'
    }],
    rows: ALL_QUESTION_TYPES.map(type => ({
      type,
      ceiling: QUESTION_TYPE_REGISTRY[type]?.cognitiveCeiling ?? 'DOK2 (default)',
      category: QUESTION_TYPE_REGISTRY[type]?.category ?? 'interactive (default)'
    }))
  })
}, {
  id: 'dok3-capable-types',
  name: 'DOK3-capabele vraagtypen (afgeleid)',
  domain: 'governance',
  file: 'src/governance/questionTypeDefinitions.ts',
  exportName: 'getDok3CapableTypes()',
  description: 'Alle vraagtypen met cognitiveCeiling === DOK3 — afgeleid, geen hardcode.',
  governs: 'Diversity-stage detectie van families zonder DOK3-capabel type.',
  consumers: ['diversityStage'],
  read: () => ({
    columns: [{
      key: 'type',
      label: 'DOK3-capabel vraagtype'
    }],
    rows: getDok3CapableTypes().map(type => ({
      type
    }))
  })
}, {
  id: 'structured-data-types',
  name: 'Vraagtypen met verplichte structured data',
  domain: 'governance',
  file: 'src/governance/questionTypeDefinitions.ts',
  exportName: 'getTypesRequiringStructuredData()',
  description: 'Vraagtypen die een namespaced data-veld vereisen (bv. columnArithmetic).',
  governs: 'pedagogyStage.detectsRegexFallback — voorkomt fragiele regex-parsing.',
  consumers: ['pedagogyStage'],
  read: () => ({
    columns: [{
      key: 'type',
      label: 'Vraagtype'
    }, {
      key: 'field',
      label: 'Vereist data-veld'
    }],
    rows: getTypesRequiringStructuredData().map(type => ({
      type,
      field: QUESTION_TYPE_REGISTRY[type]?.structuredDataField ?? '—'
    }))
  })
},
// ─── VISUALS / CONTENT ───────────────────────────────────────────────────
{
  id: 'question-type-visual-roles',
  name: 'Visual-rol per vraagtype',
  domain: 'visuals',
  file: 'src/content/questions/questionTypeRegistry.ts',
  exportName: 'QUESTION_TYPE_VISUAL_ROLE',
  description: 'Waar de visuele laag van een vraagtype leeft: embedded / contextual / scaffolding.',
  governs: 'visualStage exempt-from-stimulus set (embedded types eisen geen losse stimulusAid).',
  consumers: ['visualStage', 'rulebooks/index (auto-exempt)'],
  read: () => ({
    columns: [{
      key: 'type',
      label: 'Vraagtype'
    }, {
      key: 'role',
      label: 'Visual-rol'
    }],
    rows: (Object.entries(QUESTION_TYPE_VISUAL_ROLE) as Array<[QuestionType, string]>).map(([type, role]) => ({
      type,
      role
    }))
  })
}, {
  id: 'visual-registry',
  name: 'Visual-componentregister',
  domain: 'visuals',
  file: 'src/visuals/registry/visualRegistry.ts',
  exportName: 'VISUAL_REGISTRY',
  description: 'Runtime-map van visual-type-string naar React-component. Bepaalt welke visuals kunnen renderen.',
  governs: 'VisualAidFactory-resolutie en de schema/governance-sync van beschikbare visuals.',
  consumers: ['VisualAidFactory', 'visualPropsResolver', 'visualRegistrySync'],
  notes: 'Twee afgeleide arrays (VISUAL_REGISTRY_IMPORT_NAMES, VISUAL_AID_FACTORY_SUPPORTED_TYPES) horen Object.keys hiervan te spiegelen — drift-risico (RENDERERS_DOMAIN_RC §V-B1).',
  read: () => {
    const types = Object.keys(VISUAL_REGISTRY).sort();
    return {
      columns: [{
        key: 'index',
        label: '#'
      }, {
        key: 'type',
        label: 'Visual-type'
      }],
      rows: types.map((type, i) => ({
        index: i + 1,
        type
      }))
    };
  }
},
// ─── CONTENT ─────────────────────────────────────────────────────────────
{
  id: 'modules',
  name: 'Leermodules',
  domain: 'content',
  file: 'src/content/modules.ts',
  exportName: 'MODULES',
  description: 'De 10 canonieke leermodules met titel, beschrijving en numeriek-vlag.',
  governs: 'Module-identiteit, NUMERIC_CONTENT_MODULE_IDS en alle module-gegroepeerde afgeleiden.',
  consumers: ['taxonomyRegistry (per-module families)', 'governance pipeline (numeric analysis)', 'admin moduleMetadata'],
  read: () => ({
    columns: [{
      key: 'id',
      label: 'Module-id'
    }, {
      key: 'title',
      label: 'Titel'
    }, {
      key: 'numeric',
      label: 'Numeriek'
    }],
    rows: MODULES.map(m => ({
      id: m.id,
      title: m.title,
      numeric: m.hasNumericContent ? 'ja' : 'nee'
    }))
  })
}, {
  id: 'taxonomy-families',
  name: 'Taxonomie — families per module',
  domain: 'content',
  file: 'src/content/taxonomy/taxonomyRegistry.ts',
  exportName: 'TAXONOMY',
  description: 'De canonieke familie-index (Module → Skill → SubSkill → FamilyId). Hier samengevat als familietelling per module.',
  governs: 'Alle content-validatie en selectie die op familyId/skill/subSkill leunt.',
  consumers: ['questionLoader', 'governance schemaStage', 'mastery familyScoring', 'ldgBuilder'],
  read: () => {
    const byModule = new Map<string, number>();
    TAXONOMY.forEach(f => {
      byModule.set(f.module, (byModule.get(f.module) ?? 0) + 1);
    });
    return {
      columns: [{
        key: 'module',
        label: 'Module'
      }, {
        key: 'families',
        label: 'Aantal families'
      }],
      rows: [...Array.from(byModule.entries()).sort((a, b) => b[1] - a[1]).map(([module, families]) => ({
        module,
        families
      })), {
        module: 'TOTAAL',
        families: TAXONOMY.length
      }]
    };
  }
}, {
  id: 'question-types',
  name: 'Vraagtype-register (runtime)',
  domain: 'content',
  file: 'src/content/questions/questionTypeRegistry.ts',
  exportName: 'QUESTION_TYPE_META',
  description: 'SSOT voor welke vraagtypen bestaan + hun runtime-eigenschappen (interactief, ingebouwde visuals, CITO-eligible).',
  governs: 'Renderer-resolutie, diversity-detectie en de governance-sidecar in questionTypeDefinitions.',
  consumers: ['QuestionRendererFactory', 'diversityStage', 'questionTypeDefinitions (governance sidecar)'],
  notes: 'isCitoEligible hier is een MIRROR van de governance-policy (assessment = multiple_choice only). Drift met de policy is een bug.',
  read: () => ({
    columns: [{
      key: 'type',
      label: 'Vraagtype'
    }, {
      key: 'label',
      label: 'Label'
    }, {
      key: 'interactive',
      label: 'Interactief'
    }, {
      key: 'cito',
      label: 'CITO-eligible'
    }],
    rows: Object.values(QUESTION_TYPE_META).map(m => ({
      type: m.type,
      label: m.label,
      interactive: m.isInteractive ? 'ja' : 'nee',
      cito: m.isCitoEligible ? 'ja' : 'nee'
    }))
  })
},
// ─── PIPELINE ────────────────────────────────────────────────────────────
{
  id: 'pipeline-stages',
  name: 'Pipeline-stagenummers',
  domain: 'pipeline',
  file: 'src/governance/pipeline/stageDefinitions.ts',
  exportName: 'STAGES',
  description: 'De canonieke stagenummers (1–7) per semantische rol van de governance-pijplijn.',
  governs: 'Stage-toewijzing van elke validator en de volgorde van de pipeline-orchestrator.',
  consumers: ['orchestrator', 'contentValidators/shared', 'typeValidators/shared'],
  read: () => ({
    columns: [{
      key: 'role',
      label: 'Rol'
    }, {
      key: 'stage',
      label: 'Stage'
    }],
    rows: Object.entries(STAGES).map(([role, stage]) => ({
      role,
      stage: stage as number
    }))
  })
}, {
  id: 'issue-codes',
  name: 'Issue-codes (per stage-prefix)',
  domain: 'pipeline',
  file: 'src/governance/pipeline/issues/codes.ts',
  exportName: 'ISSUE_CODES',
  description: 'Het canonieke register van alle issue-codes die de pijplijn kan emitteren — hier samengevat als telling per stage-prefix.',
  governs: 'Welke codes validators mogen gebruiken; de IssueCode-union is hieruit afgeleid.',
  consumers: ['alle validators', 'issues/builders', 'admin governance UI'],
  read: () => {
    const byPrefix = new Map<string, number>();
    Object.keys(ISSUE_CODES).forEach(code => {
      const prefix = code.split('_')[0];
      byPrefix.set(prefix, (byPrefix.get(prefix) ?? 0) + 1);
    });
    return {
      columns: [{
        key: 'prefix',
        label: 'Stage-prefix'
      }, {
        key: 'count',
        label: 'Aantal codes'
      }],
      rows: [...Array.from(byPrefix.entries()).sort((a, b) => b[1] - a[1]).map(([prefix, count]) => ({
        prefix,
        count
      })), {
        prefix: 'TOTAAL',
        count: Object.keys(ISSUE_CODES).length
      }]
    };
  }
}, {
  id: 'severity-scale',
  name: 'Severity-ladder (15 stappen)',
  domain: 'pipeline',
  file: 'src/governance/pipeline/severityScale.ts',
  exportName: 'SEVERITY',
  description: 'De semantische 15-staps severity-ladder die issue-constructie en de admin-bucketing stuurt.',
  governs: 'Numerieke severity per issue en de 5-bucket admin-weergave (CRITICAL→INFO).',
  consumers: ['issues/builders', 'severity helpers', 'admin governance UI'],
  read: () => ({
    columns: [{
      key: 'name',
      label: 'Naam'
    }, {
      key: 'value',
      label: 'Waarde'
    }],
    rows: Object.entries(SEVERITY).sort((a, b) => (b[1] as number) - (a[1] as number)).map(([name, value]) => ({
      name,
      value: value as number
    }))
  })
}, {
  id: 'scoring-weights',
  name: 'Scoring-gewichten (gate)',
  domain: 'pipeline',
  file: 'src/governance/pipeline/severityScale.ts',
  exportName: 'DEFAULT_SCORING_WEIGHTS',
  description: 'De standaard penalty-gewichten die computeIssuePenalty toepast om de gate-score te bepalen.',
  governs: 'Gate-score = 100 − Σ penalties (blocker / coach÷divisor / info).',
  consumers: ['computeIssuePenalty', 'defaultPolicy', 'gate/evaluate'],
  read: () => ({
    columns: [{
      key: 'key',
      label: 'Gewicht'
    }, {
      key: 'value',
      label: 'Waarde'
    }],
    rows: Object.entries(DEFAULT_SCORING_WEIGHTS).map(([key, value]) => ({
      key,
      value: value as number
    }))
  })
},
// ─── DESIGN SYSTEM ───────────────────────────────────────────────────────
//
// Hier stonden vijf design-token-entries (module-palet, status-kleuren,
// rol-kleuren, mastery-schaal, radius-schaal). Ze zijn op 12 aug 2026 verwijderd
// omdat het adminportaal daarmee TWEE schermen had voor dezelfde bron: dit
// register én de Design System-pagina, beide live lezend uit
// `config/designTokens.ts`.
//
// Geen waardeverschil, maar wel al dekkingsverschil: dit register toonde 4 van
// de 7 semantische tokens en kende `SHADOW` en `BADGE_VARIANTS` niet. Zo begint
// het altijd — twee UI's over één bron blijven niet even compleet.
//
// De Design System-pagina is de overgebleven surface. Reden: een kleur is niet te
// beoordelen uit een hex-string in een tabel, en bij tokens is de swatch juist
// het punt. De provenance die hier stond (file / export / consumers) is naar die
// pagina meeverhuisd.
//
// ─── MASTERY ─────────────────────────────────────────────────────────────
{
  id: 'v11-config',
  name: 'V11_CONFIG — adaptieve-engine parameters',
  domain: 'mastery',
  file: 'src/mastery/selection/selectionConfig.ts',
  exportName: 'V11_CONFIG (runtime-overridable via v11ConfigStore)',
  description: 'De canonieke parameterset van de adaptieve selectie-engine (mastery-drempels, sessievorm, tier-window, B/M/E-boosts, diversiteit en mid-sessie difficulty-adjust). Waarden zijn live — admin-overrides muteren V11_CONFIG in-place.',
  governs: 'Family-selectie, sessieopbouw, tier-window, frustratie-swap en de mid-sessie difficulty-aanpassing.',
  consumers: ['questionSelector', 'familyScoring', 'tierWindow', 'tierProgress.canUnlock (LEVEL_UNLOCK_THRESHOLD)', 'admin Settings (override-UI)'],
  notes: 'Toont alleen de via de admin instelbare velden (V11_CONFIG_FIELDS). Niet-instelbare invarianten (MIN_DOK_FOR_TIER_BAND, MIN_TIER/MAX_TIER) zijn bewust niet runtime-tweakbaar.',
  read: () => ({
    columns: [{
      key: 'key',
      label: 'Sleutel'
    }, {
      key: 'group',
      label: 'Groep'
    }, {
      key: 'label',
      label: 'Betekenis'
    }, {
      key: 'current',
      label: 'Actueel'
    }, {
      key: 'default',
      label: 'Default'
    }],
    rows: V11_CONFIG_FIELDS.map(field => {
      const current = (V11_CONFIG as Record<string, unknown>)[field.key as string];
      return {
        key: field.key as string,
        group: field.group,
        label: field.label,
        current: typeof current === 'number' ? current : String(current),
        default: field.default
      };
    })
  })
}, {
  id: 'growth-stages',
  name: 'Groeistadia (learner-narratief)',
  domain: 'mastery',
  file: 'src/mastery/growthStages.ts',
  exportName: 'getGrowthStage()',
  description: 'De 6-staps groeiladder (Nieuw → Meester!) die een 0–100 mastery-score vertaalt naar emoji, label en kleur voor de leerling-schil. Banden hieronder worden live afgeleid door de functie te bevragen.',
  governs: 'GrowthStageIcon, voortgang/achievements-presentatie, sessie-celebraties en MasteryRing-emoji in learner-context.',
  consumers: ['MasteryRing (learner)', 'GrowthThisWeekSection', 'CelebrationMoments'],
  notes: 'Alleen learner-schil. Parent/teacher gebruiken de 4-staps volwassen-vocabulaire uit portals/shared/masteryBands.ts — bewust gescheiden.',
  read: () => {
    // Derive each band's score-range live by sampling the SSOT function,
    // so the table can never drift from the actual thresholds.
    const byLevel = new Map<number, {
      emoji: string;
      label: string;
      min: number;
      max: number;
    }>();
    for (let score = 0; score <= 100; score++) {
      const stage = getGrowthStage(score);
      const existing = byLevel.get(stage.level);
      if (!existing) {
        byLevel.set(stage.level, {
          emoji: stage.emoji,
          label: stage.label,
          min: score,
          max: score
        });
      } else {
        existing.max = score;
      }
    }
    return {
      columns: [{
        key: 'level',
        label: 'Niveau'
      }, {
        key: 'emoji',
        label: 'Emoji'
      }, {
        key: 'label',
        label: 'Label'
      }, {
        key: 'range',
        label: 'Mastery-bereik'
      }],
      rows: Array.from(byLevel.entries()).sort((a, b) => a[0] - b[0]).map(([level, band]) => ({
        level,
        emoji: band.emoji,
        label: band.label,
        range: band.min === band.max ? `${band.min}` : `${band.min}–${band.max}`
      }))
    };
  }
}, {
  id: 'proxy-difficulty-contract',
  name: 'Proxy-contract — difficulty (IRT ↔ Cito)',
  domain: 'mastery',
  file: 'src/shared/proxyContracts/difficulty.ts',
  exportName: 'IRTDifficulty / CitoPValue + conversie-boundary',
  description: 'De branded difficulty-types met hun enige conversie-grens tussen de IRT-style moeilijkheidsindex en de Cito p-waarde. Voorkomt "naked number"-drift tussen contexten (FG1).',
  governs: 'De moeilijkheidsindex-grens [min, max] en de IRT→Cito-vertaling die elke difficulty-consument deelt.',
  consumers: ['schemaStage (isDifficultyConsistentWithTier)', 'citoLevelUtils', 'masteryProjection'],
  notes: 'Onderdeel van de gedeelde proxyContracts-laag (ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §F0.A). De conversie leeft op één plek — directe reads buiten de boundary zijn een drift-risico.',
  read: () => {
    // Sample the conversion boundary live across the valid index range.
    const samples = [DIFFICULTY_INDEX_MIN, 0.3, 0.5, 0.7, DIFFICULTY_INDEX_MAX];
    return {
      columns: [{
        key: 'index',
        label: 'Moeilijkheidsindex (IRT)'
      }, {
        key: 'cito',
        label: 'Cito p-waarde'
      }],
      rows: samples.map(raw => {
        const irt = toIRTDifficulty(raw);
        const cito = irtDifficultyToCitoPValue(irt);
        return {
          index: Number(raw).toFixed(2),
          cito: Number(cito).toFixed(2)
        };
      })
    };
  }
}];

/** Returns the full SSOT catalog. */
export function getSsotRegistry(): SsotEntry[] {
  return SSOT_ENTRIES;
}

/**
 * Derived count of catalogued SSOTs in de GOVERNANCE-laag — never hand-maintained.
 *
 * LET OP: dit is niet het totaal dat de beheerder ziet. De SSOT Registry-pagina
 * voegt hier de platform-bronnen aan toe (portals/admin/system/ssot), die niet in
 * deze laag mogen staan omdat governance niets van portalen of de database-client
 * mag weten. Gebruik dit getal dus niet als "aantal bronnen in het product".
 */
export const SSOT_REGISTRY_COUNT = SSOT_ENTRIES.length;

/** Lookup a single SSOT entry by id. */
export function getSsotEntry(id: string): SsotEntry | undefined {
  return SSOT_ENTRIES.find(e => e.id === id);
}